import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Switch,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { useTranslation } from "react-i18next";
import { TimePicker } from "components/Shared/mui-formik-inputs";
import {
  WEEK_DAYS,
  DAY_LABELS,
  normalizeOpeningHours,
  getDayKeyFromDate,
  validateDayIntervals,
} from "./openingHoursUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: "24px",
    marginBottom: "24px",
  },
  heading: {
    font: "normal normal 500 22px/32px Roboto",
    color: "#121212",
    marginBottom: "20px",
  },
  sameEveryDayCard: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #D0D0D0",
    borderRadius: "2px",
    padding: "6px 16px",
    minWidth: "260px",
    maxWidth: "340px",
    backgroundColor: "#FAFAFA",
    marginBottom: "24px",
  },
  sameEveryDayLabel: {
    fontSize: "14px",
    color: "#4A4A4A",
    fontWeight: 500,
  },
  daysContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  horizontalDaysContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "20px",
  },
  sameEveryDayIntervalsContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  dayRow: {
    display: "flex",
    alignItems: "flex-start",
    minHeight: "48px",
    gap: "16px",
    flexWrap: "wrap",
  },
  dayBadge: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "14px",
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.2s ease-in-out",
    border: "none",
    outline: "none",
    "&:hover": {
      transform: "scale(1.06)",
    },
  },
  dayBadgeActive: {
    backgroundColor: "#6F9CEB",
    color: "#FFFFFF",
    boxShadow: "0px 2px 4px rgba(111, 156, 235, 0.35)",
  },
  dayBadgeInactive: {
    backgroundColor: "#E0E0E0",
    color: "#FFFFFF",
  },
  intervalsContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  intervalItemWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  intervalGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  timePickerSlot: {
    width: "130px",
    margin: "0 !important",
    "& .MuiFilledInput-root": {
      backgroundColor: "#E4E4E4",
      borderRadius: "2px",
      height: "40px",
      paddingRight: "8px",
      transition: "background-color 0.2s",
      "&:hover": {
        backgroundColor: "#DADADA",
      },
      "&.Mui-focused": {
        backgroundColor: "#D5D5D5",
      },
    },
    "& .MuiFilledInput-input": {
      padding: "8px 0 8px 12px",
      fontSize: "14px",
      fontWeight: 500,
      color: "#333333",
    },
    "& .MuiIconButton-root": {
      padding: "4px",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
      color: "#5A5A5A",
    },
  },
  timePickerSlotError: {
    "& .MuiFilledInput-root": {
      border: "1px solid #D32F2F !important",
      backgroundColor: "#FFEBEE !important",
      "&:hover": {
        backgroundColor: "#FFCDD2 !important",
      },
      "&.Mui-focused": {
        backgroundColor: "#FFCDD2 !important",
      },
    },
  },
  intervalErrorText: {
    color: "#D32F2F",
    fontSize: "12px",
    fontWeight: 500,
    marginTop: "4px",
    marginLeft: "2px",
    maxWidth: "280px",
    lineHeight: 1.2,
  },
  dashSeparator: {
    color: "#6E6E6E",
    fontSize: "16px",
    fontWeight: "bold",
    userSelect: "none",
  },
  addButton: {
    width: "32px",
    height: "32px",
    backgroundColor: "#6F9CEB",
    color: "#FFFFFF",
    borderRadius: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    padding: 0,
    transition: "background-color 0.2s, transform 0.1s",
    "&:hover": {
      backgroundColor: "#5B8ADA",
      transform: "scale(1.05)",
    },
    "&:disabled": {
      backgroundColor: "#CCCCCC",
      cursor: "not-allowed",
    },
  },
  deleteButton: {
    padding: "4px",
    color: "#8E8E8E",
    "&:hover": {
      color: "#D32F2F",
    },
  },
}));

const OpeningHoursEditor = ({
  value,
  onChange,
  readOnly = false,
  singleDayMode = false,
  selectedDate = null,
  selectedDayKey = null,
  title,
  hideTitle = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const openingHours = useMemo(() => normalizeOpeningHours(value), [value]);

  const targetDayKey = useMemo(() => {
    if (selectedDayKey) return selectedDayKey.toUpperCase();
    if (selectedDate) return getDayKeyFromDate(selectedDate);
    return "MO";
  }, [selectedDayKey, selectedDate]);

  // Extract reference/common intervals when same_every_day is active
  const commonIntervals = useMemo(() => {
    const activeDay = WEEK_DAYS.find((d) => openingHours.schedule[d]?.active);
    if (activeDay && openingHours.schedule[activeDay]?.intervals?.length) {
      return openingHours.schedule[activeDay].intervals;
    }
    const anyDayWithIntervals = WEEK_DAYS.find((d) => openingHours.schedule[d]?.intervals?.length);
    if (anyDayWithIntervals && openingHours.schedule[anyDayWithIntervals]?.intervals?.length) {
      return openingHours.schedule[anyDayWithIntervals].intervals;
    }
    return [{ from: "09:00", to: "16:00" }];
  }, [openingHours]);

  const commonErrors = useMemo(() => {
    return validateDayIntervals(commonIntervals);
  }, [commonIntervals]);

  const updateSchedule = (updater) => {
    if (readOnly) return;
    const newOpeningHours = typeof updater === "function" ? updater(openingHours) : updater;
    if (onChange) {
      onChange(newOpeningHours);
    }
  };

  const handleSameEveryDayToggle = (e) => {
    const checked = e.target.checked;
    updateSchedule((prev) => {
      const newSchedule = { ...prev.schedule };
      if (checked) {
        // Find reference intervals (e.g. from Monday or first active day)
        const refDay = WEEK_DAYS.find((d) => newSchedule[d]?.active) || "MO";
        const refIntervals = newSchedule[refDay]?.intervals || [{ from: "09:00", to: "16:00" }];
        WEEK_DAYS.forEach((d) => {
          newSchedule[d] = {
            ...newSchedule[d],
            intervals: JSON.parse(JSON.stringify(refIntervals)),
          };
        });
      }
      return {
        ...prev,
        same_every_day: checked,
        schedule: newSchedule,
      };
    });
  };

  const handleDayToggle = (day) => {
    if (readOnly) return;
    updateSchedule((prev) => {
      const currentDay = prev.schedule[day] || { active: false, intervals: [{ from: "09:00", to: "16:00" }] };
      const nextActive = !currentDay.active;
      let newIntervals = currentDay.intervals?.length ? currentDay.intervals : [{ from: "09:00", to: "16:00" }];

      if (nextActive && prev.same_every_day) {
        const refDay = WEEK_DAYS.find((d) => d !== day && prev.schedule[d]?.active);
        if (refDay && prev.schedule[refDay]?.intervals) {
          newIntervals = JSON.parse(JSON.stringify(prev.schedule[refDay].intervals));
        } else {
          newIntervals = JSON.parse(JSON.stringify(commonIntervals));
        }
      }

      return {
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: {
            ...currentDay,
            active: nextActive,
            intervals: newIntervals,
          },
        },
      };
    });
  };

  // Handler when same_every_day is ON
  const handleCommonTimeChange = (index, field, newTime) => {
    if (readOnly) return;
    updateSchedule((prev) => {
      const newSchedule = { ...prev.schedule };
      const updatedCommon = [...commonIntervals];
      updatedCommon[index] = {
        ...updatedCommon[index],
        [field]: newTime,
      };

      WEEK_DAYS.forEach((d) => {
        newSchedule[d] = {
          ...newSchedule[d],
          intervals: JSON.parse(JSON.stringify(updatedCommon)),
        };
      });

      return {
        ...prev,
        schedule: newSchedule,
      };
    });
  };

  const handleCommonAddInterval = () => {
    if (readOnly) return;
    updateSchedule((prev) => {
      const newSchedule = { ...prev.schedule };
      const lastInterval = commonIntervals[commonIntervals.length - 1];
      let newFrom = "13:00";
      let newTo = "16:00";
      if (lastInterval && lastInterval.to) {
        newFrom = lastInterval.to;
        const [h, m] = newFrom.split(":").map(Number);
        const nextHour = Math.min(23, (h || 12) + 4);
        newTo = `${String(nextHour).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
      }

      const updatedCommon = [...commonIntervals, { from: newFrom, to: newTo }];
      WEEK_DAYS.forEach((d) => {
        newSchedule[d] = {
          ...newSchedule[d],
          intervals: JSON.parse(JSON.stringify(updatedCommon)),
        };
      });

      return {
        ...prev,
        schedule: newSchedule,
      };
    });
  };

  const handleCommonRemoveInterval = (index) => {
    if (readOnly) return;
    updateSchedule((prev) => {
      const newSchedule = { ...prev.schedule };
      const updatedCommon = commonIntervals.filter((_, i) => i !== index);
      const finalCommon = updatedCommon.length ? updatedCommon : [{ from: "09:00", to: "16:00" }];

      WEEK_DAYS.forEach((d) => {
        newSchedule[d] = {
          ...newSchedule[d],
          intervals: JSON.parse(JSON.stringify(finalCommon)),
        };
      });

      return {
        ...prev,
        schedule: newSchedule,
      };
    });
  };

  // Handler when same_every_day is OFF
  const handleTimeChange = (day, index, field, newTime) => {
    if (readOnly) return;
    updateSchedule((prev) => {
      const newSchedule = { ...prev.schedule };
      const currentDay = newSchedule[day] || { active: true, intervals: [{ from: "09:00", to: "16:00" }] };
      const updatedIntervals = [...currentDay.intervals];

      updatedIntervals[index] = {
        ...updatedIntervals[index],
        [field]: newTime,
      };

      newSchedule[day] = {
        ...currentDay,
        intervals: updatedIntervals,
      };

      return {
        ...prev,
        schedule: newSchedule,
      };
    });
  };

  const handleAddInterval = (day) => {
    if (readOnly) return;
    updateSchedule((prev) => {
      const newSchedule = { ...prev.schedule };
      const currentDay = newSchedule[day] || { active: true, intervals: [] };
      const currentIntervals = currentDay.intervals || [];

      const lastInterval = currentIntervals[currentIntervals.length - 1];
      let newFrom = "13:00";
      let newTo = "16:00";
      if (lastInterval && lastInterval.to) {
        newFrom = lastInterval.to;
        const [h, m] = newFrom.split(":").map(Number);
        const nextHour = Math.min(23, (h || 12) + 4);
        newTo = `${String(nextHour).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
      }

      const updatedIntervals = [...currentIntervals, { from: newFrom, to: newTo }];
      newSchedule[day] = {
        ...currentDay,
        intervals: updatedIntervals,
      };

      return {
        ...prev,
        schedule: newSchedule,
      };
    });
  };

  const handleRemoveInterval = (day, index) => {
    if (readOnly) return;
    updateSchedule((prev) => {
      const newSchedule = { ...prev.schedule };
      const currentDay = newSchedule[day] || { active: true, intervals: [] };
      const updatedIntervals = currentDay.intervals.filter((_, i) => i !== index);

      newSchedule[day] = {
        ...currentDay,
        intervals: updatedIntervals.length ? updatedIntervals : [{ from: "09:00", to: "16:00" }],
      };

      return {
        ...prev,
        schedule: newSchedule,
      };
    });
  };

  const renderDayRow = (day) => {
    const dayConfig = openingHours.schedule[day] || { active: false, intervals: [{ from: "09:00", to: "16:00" }] };
    const isActive = Boolean(dayConfig.active);
    const intervals = dayConfig.intervals || [{ from: "09:00", to: "16:00" }];
    const dayErrors = isActive ? validateDayIntervals(intervals) : [];

    return (
      <div key={day} className={classes.dayRow}>
        <button
          type="button"
          onClick={() => handleDayToggle(day)}
          disabled={readOnly}
          className={`${classes.dayBadge} ${isActive ? classes.dayBadgeActive : classes.dayBadgeInactive}`}
          title={`${t(DAY_LABELS[day])}: ${isActive ? t("Active (Click to close)") : t("Closed (Click to open)")}`}
        >
          {t(day)}
        </button>

        {isActive ? (
          <div className={classes.intervalsContainer}>
            {intervals.map((interval, index) => {
              const err = dayErrors[index];
              return (
                <div key={index} className={classes.intervalItemWrapper}>
                  <div className={classes.intervalGroup}>
                    <TimePicker
                      value={interval.from || "09:00"}
                      disabled={readOnly}
                      onChange={(val) => handleTimeChange(day, index, "from", val)}
                      className={`${classes.timePickerSlot} ${err ? classes.timePickerSlotError : ""}`}
                      inputVariant="filled"
                      ampm={false}
                    />

                    <span className={classes.dashSeparator}>—</span>

                    <TimePicker
                      value={interval.to || "16:00"}
                      disabled={readOnly}
                      onChange={(val) => handleTimeChange(day, index, "to", val)}
                      className={`${classes.timePickerSlot} ${err ? classes.timePickerSlotError : ""}`}
                      inputVariant="filled"
                      ampm={false}
                    />

                    {intervals.length > 1 && !readOnly && (
                      <IconButton
                        size="small"
                        className={classes.deleteButton}
                        title={t("Remove interval")}
                        onClick={() => handleRemoveInterval(day, index)}
                      >
                        <DeleteIcon style={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </div>
                  {err && (
                    <Typography className={classes.intervalErrorText}>
                      {t(err)}
                    </Typography>
                  )}
                </div>
              );
            })}

            {!readOnly && (
              <button
                type="button"
                className={classes.addButton}
                style={{ marginTop: "4px" }}
                title={t("Add interval")}
                onClick={() => handleAddInterval(day)}
              >
                <AddIcon style={{ fontSize: 20 }} />
              </button>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  if (singleDayMode) {
    const dayConfig = openingHours.schedule[targetDayKey] || { active: true, intervals: [{ from: "09:00", to: "16:00" }] };
    const intervals = dayConfig.intervals?.length ? dayConfig.intervals : [{ from: "09:00", to: "16:00" }];
    const dayErrors = validateDayIntervals(intervals);

    return (
      <Box my={2}>
        {!hideTitle && (
          <Typography className={classes.heading} variant="h5" style={{ fontSize: "18px", marginBottom: "12px", marginTop: "12px" }}>
            {title || `${t("Opening Hours")} (${t(DAY_LABELS[targetDayKey])})`}
          </Typography>
        )}
        <div className={classes.intervalsContainer}>
          {intervals.map((interval, index) => {
            const err = dayErrors[index];
            return (
              <div key={index} className={classes.intervalItemWrapper}>
                <div className={classes.intervalGroup}>
                  <TimePicker
                    value={interval.from || "09:00"}
                    disabled={readOnly}
                    onChange={(val) => handleTimeChange(targetDayKey, index, "from", val)}
                    className={`${classes.timePickerSlot} ${err ? classes.timePickerSlotError : ""}`}
                    inputVariant="filled"
                    ampm={false}
                  />

                  <span className={classes.dashSeparator}>—</span>

                  <TimePicker
                    value={interval.to || "16:00"}
                    disabled={readOnly}
                    onChange={(val) => handleTimeChange(targetDayKey, index, "to", val)}
                    className={`${classes.timePickerSlot} ${err ? classes.timePickerSlotError : ""}`}
                    inputVariant="filled"
                    ampm={false}
                  />
                </div>
                {err && (
                  <Typography className={classes.intervalErrorText}>
                    {t(err)}
                  </Typography>
                )}
              </div>
            );
          })}
        </div>
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      {!hideTitle && (
        <Typography className={classes.heading} variant="h5">
          {title || t("Opening Hours")}
        </Typography>
      )}

      <div className={classes.sameEveryDayCard}>
        <span className={classes.sameEveryDayLabel}>{t("Same Every Day")}</span>
        <Switch
          checked={Boolean(openingHours.same_every_day)}
          onChange={handleSameEveryDayToggle}
          color="primary"
          disabled={readOnly}
          size="small"
        />
      </div>

      {openingHours.same_every_day ? (
        <div>
          {/* Horizontal row of day badges */}
          <div className={classes.horizontalDaysContainer}>
            {WEEK_DAYS.map((day) => {
              const dayConfig = openingHours.schedule[day] || { active: false };
              const isActive = Boolean(dayConfig.active);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayToggle(day)}
                  disabled={readOnly}
                  className={`${classes.dayBadge} ${isActive ? classes.dayBadgeActive : classes.dayBadgeInactive}`}
                  title={`${t(DAY_LABELS[day])}: ${isActive ? t("Active (Click to close)") : t("Closed (Click to open)")}`}
                >
                  {t(day)}
                </button>
              );
            })}
          </div>

          {/* Single time interval row for all active days */}
          <div className={classes.sameEveryDayIntervalsContainer}>
            {commonIntervals.map((interval, index) => {
              const err = commonErrors[index];
              return (
                <div key={index} className={classes.intervalItemWrapper}>
                  <div className={classes.intervalGroup}>
                    <TimePicker
                      value={interval.from || "09:00"}
                      disabled={readOnly}
                      onChange={(val) => handleCommonTimeChange(index, "from", val)}
                      className={`${classes.timePickerSlot} ${err ? classes.timePickerSlotError : ""}`}
                      inputVariant="filled"
                      ampm={false}
                    />

                    <span className={classes.dashSeparator}>—</span>

                    <TimePicker
                      value={interval.to || "16:00"}
                      disabled={readOnly}
                      onChange={(val) => handleCommonTimeChange(index, "to", val)}
                      className={`${classes.timePickerSlot} ${err ? classes.timePickerSlotError : ""}`}
                      inputVariant="filled"
                      ampm={false}
                    />

                    {commonIntervals.length > 1 && !readOnly && (
                      <IconButton
                        size="small"
                        className={classes.deleteButton}
                        title={t("Remove interval")}
                        onClick={() => handleCommonRemoveInterval(index)}
                      >
                        <DeleteIcon style={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </div>
                  {err && (
                    <Typography className={classes.intervalErrorText}>
                      {t(err)}
                    </Typography>
                  )}
                </div>
              );
            })}

            {!readOnly && (
              <button
                type="button"
                className={classes.addButton}
                style={{ marginTop: "4px" }}
                title={t("Add interval")}
                onClick={handleCommonAddInterval}
              >
                <AddIcon style={{ fontSize: 20 }} />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className={classes.daysContainer}>
          {WEEK_DAYS.map((day) => renderDayRow(day))}
        </div>
      )}
    </div>
  );
};

export default OpeningHoursEditor;
